import Logo from 'assett/icons/logo.svg';
import SkipToMainContentLink from 'components/commons/SkipToMainContentLink';
import useDevice from 'hooks/DesktopDevice.hook';
import useDialogConfirm from 'hooks/DialogConfirm.hook';
import { useResetPage } from 'hooks/utils.hook';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Outlet, useLocation, useParams } from 'react-router-dom';
import { RootState } from 'store/store';
import { Box } from 'styles';
import CopyButton from 'styles/button/CopyButton';
import Container from 'styles/container/Container';
import Stack from 'styles/stack/Stack';
import Typography from 'styles/typography/Typograpy';
import { VERSION } from 'types/dialogtypes';
import { PATHNAMES } from 'utils/constants';
import { useTranslation } from 'utils/translations';
import { FooterDesktop } from '../FooterDesktop/FooterDesktop';
import Sidebar from '../Sidebar/Sidebar.component';
import Breadcrumbs from './Breadcrumbs.component';

export default function SharedLayout() {
	const { t: tClientLabels } = useTranslation('', {
		keyPrefix: 'clientLabels',
	});
	const location = useLocation();
	const confirmDialog = useDialogConfirm();

	const [windowHeight, setWindowHeight] = useState<number>(window.innerHeight);

	const { isDesktop } = useDevice();
	const { Uuid, id } = useParams();
	const { claimData } = useSelector((store: RootState) => store.claim);
	const { member } = useSelector((store: RootState) => store.member);

	const getPageTitle = (e: string) => {
		if (decodeURI(e) === id) {
			if (!!id) {
				if (location.pathname.includes(PATHNAMES.VIEW_PAYMENT_HISTORY)) {
					return `${tClientLabels('payment.breadcrumb')} #${id}`;
				} else if (
					!location.pathname.includes('check-and-submit') ||
					(location.pathname.includes('check-and-submit') &&
						location.search.includes('claimUUID'))
				) {
					return `${tClientLabels('claim.breadcrumb')} #${
						claimData.externalClaimId
					}`;
				}
			}
			return id;
		}

		if (decodeURI(e) === Uuid) {
			if (
				location.pathname.includes(PATHNAMES.VIEW_CLAIMS) ||
				location.pathname.includes(PATHNAMES.CLAIMS_NOTIFICATIONS)
			) {
				return `${tClientLabels('claim.breadcrumb')} #${claimData.externalClaimId}`;
			}
		}

		if (
			location.pathname.includes(PATHNAMES.EXPLANATION_OF_PAYMENT) ||
			location.pathname.includes(PATHNAMES.GROUP_INVOICE_ASSESSMENT) ||
			location.pathname.includes(PATHNAMES.ASSESSMENT_FEE_INVOICE) ||
			location.pathname.includes(id?.replace('|', '%7C') as string)
		) {
			return `${tClientLabels('payment.breadcrumb')} #${id} `;
		}

		if (location.pathname.includes(PATHNAMES.DOCUMENTATION)) {
			return tClientLabels('documentation.pageTitle');
		}

		if (
			location.pathname.includes(PATHNAMES.CHECK_AND_SUBMIT) &&
			location.pathname.split('/').length === 3
		) {
			return member?.memberName;
		}

		if (
			location.pathname.includes(PATHNAMES.CHECK_AND_SUBMIT) &&
			location.search.includes('claimUUID')
		) {
			return `${tClientLabels('claim.breadcrumb')} #${claimData.externalClaimId}`;
		}

		return tClientLabels(`${e}.pageTitle`);
	};

	const pageTitle = getPageTitle(location.pathname.split('/').at(-1)!);

	useEffect(
		() => {
			setWindowHeight(Math.max(windowHeight, window.innerHeight));
		},
		// eslint-disable-next-line
		[window.innerHeight],
	);

	useResetPage();

	return (
		<>
			<SkipToMainContentLink />
			<Container
				sx={{
					height: !isDesktop ? `${windowHeight}px` : '100vh',
					overflow: 'hidden',
				}}
			>
				<Sidebar />

				<Stack width={'100%'}>
					<Stack direction={'row'} justifyContent={'space-between'}>
						<Stack sx={{ mt: { sm: 3.75, lg: 8.75 } }}>
							<Stack direction={'row'} alignItems={'center'} gap={'20px'}>
								<Typography variant="title2" role="heading" aria-level={1}>
									{pageTitle}
								</Typography>

								{(!!Uuid && location.pathname.includes(PATHNAMES.VIEW_CLAIMS)) ||
								(location.pathname.includes('check-and-submit') &&
									location.search.includes('claimUUID')) ? (
									<CopyButton text={claimData.externalClaimId} />
								) : null}
							</Stack>

							<Breadcrumbs
								claimUuid={claimData.claimUUID || ''}
								externalClaimId={claimData.externalClaimId}
							/>
						</Stack>
						<Stack alignItems={'flex-start'} sx={{ mt: { sm: 6.25, lg: 8.75 } }}>
							<Box width="48px" height="49px">
								<img
									onClick={() => {
										confirmDialog.open(VERSION);
									}}
									src={Logo}
									alt={`${tClientLabels('commons.logo.alt')}`}
									loading="lazy"
									style={{
										width: '200%',
										height: '200%',
										transformOrigin: '0 0',
										transform: 'scale(0.5)',
									}}
								/>
							</Box>
						</Stack>
					</Stack>
					<Stack
						sx={{
							height: `calc(${windowHeight}px - ${!!isDesktop ? '240px' : '204px'})`,
							maxWidth: `calc(100vw - ${!!isDesktop ? '260px' : '180px'})`,
						}}
					>
						<Outlet />
					</Stack>

					<FooterDesktop />
				</Stack>
			</Container>
		</>
	);
}
